import cls from "classnames"
import { chunk, clamp } from "lodash-es"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { getEventByGalleryId } from "@/data/events"
import { i18n } from "@/i18n"
import { useGalleryStore, useModalStore } from "@/store"
import { getCachedDialogues, resolveDialogues } from "@/store/event"
import { collectClickShareButton } from "@/utils/event"
import { ImagePreview } from "../ImagePreview"
import { Modal } from "../Modal"
import StrokeText from "../StrokeText"

import styles from "./index.module.scss"

const PAGE_SIZE = 4

export const GalleryModal: React.FC = () => {
    const open = useModalStore((state) => state.galleryOpen)
    const onCancel = useModalStore((state) => state.closeGallery)
    const openShare = useModalStore((state) => state.openShare)

    const [currentPage, setPage] = useState(0)
    const [previewSrc, setPreviewSrc] = useState<string | null>(null)

    const { items } = useGalleryStore()
    const prevPageRef = useRef(currentPage)

    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE))

    const pageItems = useMemo(() => {
        const start = currentPage * PAGE_SIZE
        return items.slice(start, start + PAGE_SIZE)
    }, [items, currentPage])

    useEffect(() => {
        if (prevPageRef.current !== currentPage) {
            prevPageRef.current = currentPage
        }
    }, [currentPage])

    useEffect(() => {
        if (!open) {
            setPage(0)
        }
    }, [open])

    const handlePage = useCallback(
        (page: number) => {
            setPage((val) => clamp(val + page, 0, totalPages - 1))
        },
        [totalPages],
    )

    return (
        <>
            <Modal
                open={open}
                size="gallery"
                onCancel={onCancel}
                showCancelBtn={false}
                showOkBtn={false}
                closeButtonClassName={styles.close}
            >
                <div className={styles.grid}>
                    {chunk(pageItems, 2).map((row, i) => (
                        <div key={i} className={styles.row}>
                            {row.map((item) => {
                                const itemContent = (
                                    <div className={styles.content}>
                                        {item.unlocked ? (
                                            <img
                                                className={styles.thumbnail}
                                                src={item.thumbnail}
                                                alt={item.title}
                                                draggable={false}
                                                onClick={() =>
                                                    setPreviewSrc(item.image)
                                                }
                                            />
                                        ) : null}
                                        <button
                                            type="button"
                                            className={styles.share}
                                            aria-label="share"
                                            disabled={!item.unlocked}
                                            onClick={() => {
                                                const event =
                                                    getEventByGalleryId(item.id)

                                                let picked:
                                                    | ReturnType<
                                                          typeof resolveDialogues
                                                      >[number]
                                                    | undefined

                                                if (event) {
                                                    const cached =
                                                        getCachedDialogues(
                                                            event.id,
                                                        )
                                                    if (cached) {
                                                        picked =
                                                            cached[
                                                                Math.floor(
                                                                    Math.random() *
                                                                        cached.length,
                                                                )
                                                            ]
                                                    } else {
                                                        const allEntries =
                                                            event.choices.flatMap(
                                                                (c) =>
                                                                    c.nextDialogues,
                                                            )
                                                        if (
                                                            allEntries.length >
                                                            0
                                                        ) {
                                                            const resolved =
                                                                resolveDialogues(
                                                                    allEntries,
                                                                )
                                                            picked =
                                                                resolved[
                                                                    Math.floor(
                                                                        Math.random() *
                                                                            resolved.length,
                                                                    )
                                                                ]
                                                        }
                                                    }
                                                }

                                                if (picked) {
                                                    openShare({
                                                        galleryId: item.id,
                                                        dialogText: i18n.get(
                                                            picked.text as never,
                                                        ),
                                                        speaker: picked.speaker,
                                                        expression:
                                                            picked.expression,
                                                    })
                                                } else {
                                                    openShare({
                                                        galleryId: item.id,
                                                        hideDialog: true,
                                                    })
                                                }

                                                collectClickShareButton(
                                                    "game",
                                                    item.id,
                                                )
                                            }}
                                        >
                                            <StrokeText
                                                text={i18n.get(
                                                    "map.gallery.share",
                                                )}
                                                fontFamily="source-han-sans-japanese"
                                                fontWeight={700}
                                                fontSize={22}
                                                fillColor="#fff"
                                                borderWidth={4}
                                                borderColor="#564426"
                                                offsetY={0}
                                                padding={3}
                                                baselineAligned
                                                className={styles.shareText}
                                            />
                                        </button>
                                    </div>
                                )

                                if (!item.unlocked) {
                                    return (
                                        <div
                                            key={item.id}
                                            className={styles.item}
                                        >
                                            {itemContent}
                                        </div>
                                    )
                                }

                                return (
                                    <div
                                        key={item.id}
                                        className={cls(
                                            styles.item,
                                            styles.unlocked,
                                        )}
                                    >
                                        {itemContent}
                                    </div>
                                )
                            })}
                        </div>
                    ))}
                </div>
                <div className={styles.title} />
                <button
                    type="button"
                    className={cls(styles.arrow, styles.prev)}
                    disabled={currentPage === 0}
                    onClick={() => handlePage(-1)}
                    aria-label="prev"
                />
                <button
                    type="button"
                    className={cls(styles.arrow, styles.next)}
                    disabled={currentPage === totalPages - 1}
                    onClick={() => handlePage(1)}
                    aria-label="next"
                />
                <div className={styles.tips}>
                    {i18n.getArray("map.gallery.shareTips").map((text, i) => (
                        <span
                            key={i}
                            className={cls(
                                styles.text,
                                i === 0 && styles.highlight,
                            )}
                        >
                            {text}
                        </span>
                    ))}
                </div>
            </Modal>
            <ImagePreview
                open={!!previewSrc}
                src={previewSrc}
                onClose={() => setPreviewSrc(null)}
            />
        </>
    )
}
